"use client";

import { useId, useState } from "react";

import cn from "classnames";

import { brown } from "@/app/font";
import type { GetHomePageQueryResult } from "@/sanity.types";

import { AdtTwoWaysCard } from "./adtTwoWaysCard.component";

export const AdtTwoWays = ({ block }: { block: AdtTwoWaysBlock }) => {
  const options = (block.options ?? []).filter(
    (option): option is AdtTwoWaysOption & { _key: string } =>
      Boolean(option?._key && option.content?.length),
  );
  const [selectedId, setSelectedId] = useState(options[0]?._key ?? "");
  const baseId = useId();

  if (!block.heading || options.length === 0) return null;

  const activeId = options.some((option) => option._key === selectedId)
    ? selectedId
    : options[0]._key;

  const onTabKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    const currentIndex = options.findIndex(
      (option) => option._key === activeId,
    );
    const delta = event.key === "ArrowRight" ? 1 : -1;
    const next =
      options[(currentIndex + delta + options.length) % options.length];
    if (!next) return;
    setSelectedId(next._key);
    document.getElementById(`${baseId}-tab-${next._key}`)?.focus();
  };

  const renderSwitcher = () => {
    if (options.length < 2) return null;

    return (
      <div
        role="tablist"
        aria-label="Home security options"
        data-testid="adt-two-ways-switcher"
        className="bg-blu-ink flex rounded-full p-1.5 lg:hidden"
        onKeyDown={onTabKeyDown}>
        {options.map((option) => {
          const isSelected = option._key === activeId;
          return (
            <button
              key={option._key}
              id={`${baseId}-tab-${option._key}`}
              type="button"
              role="tab"
              aria-selected={isSelected}
              aria-controls={`${baseId}-panel-${option._key}`}
              tabIndex={isSelected ? 0 : -1}
              className={cn(
                brown.className,
                "focus-visible:ring-primary focus-visible:ring-offset-blu-ink flex-1 rounded-full px-4 py-3 text-[18px] leading-none font-bold transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
                {
                  "bg-primary text-secondary": isSelected,
                  "text-white": !isSelected,
                },
              )}
              onClick={() => setSelectedId(option._key)}>
              {option.tabLabel}
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <section
      id="adt-two-ways"
      data-testid="adt-two-ways"
      aria-labelledby={`${baseId}-title`}
      className="w-full bg-white px-4 py-10 sm:px-8 sm:py-14 lg:py-16">
      <div className="mx-auto flex w-full max-w-336 flex-col gap-6 lg:gap-8">
        <div className="flex flex-col gap-3">
          {block.eyebrow ? (
            <p className="text-adt-blue text-[13px] font-bold tracking-[0.06em] uppercase sm:text-sm">
              {block.eyebrow}
            </p>
          ) : null}
          <h2
            id={`${baseId}-title`}
            className={`${brown.className} text-greyscale-headline text-[42px] leading-[1.05] font-bold tracking-tight sm:text-[56px]`}>
            {block.heading}
          </h2>
        </div>

        {renderSwitcher()}

        <div
          data-testid="adt-two-ways-cards"
          className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:gap-5">
          {options.map((option, index) => (
            <AdtTwoWaysCard
              key={option._key}
              option={option}
              panelId={`${baseId}-panel-${option._key}`}
              labelledBy={`${baseId}-tab-${option._key}`}
              isSelected={option._key === activeId}
              ctaVariant={index === 0 ? "filled" : "outline"}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export type AdtTwoWaysBlock = Extract<
  NonNullable<NonNullable<GetHomePageQueryResult>["pageBuilder"]>[number],
  { _type: "adtTwoWays" }
>;

export type AdtTwoWaysOption = NonNullable<
  NonNullable<AdtTwoWaysBlock["options"]>[number]
>;
